import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { Shield, Loader2, RefreshCw, Smartphone, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

// Inline 2FA form (renders inside parent dialog, no own Dialog wrapper)
export function TwoFactorInline({ pendingToken, emailHint, suspicious, suspiciousReasons, onVerified, onCancel }) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const [cooldown, setCooldown] = useState(60);
  const inputRefs = useRef([]);

  useEffect(() => {
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleInput = (idx, value) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...code];
    next[idx] = value.slice(-1);
    setCode(next);
    if (value && idx < 5) inputRefs.current[idx + 1]?.focus();
    if (value && idx === 5 && next.every(d => d !== '')) handleVerify(next.join(''));
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0) inputRefs.current[idx - 1]?.focus();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(''));
      inputRefs.current[5]?.focus();
      handleVerify(pasted);
      e.preventDefault();
    }
  };

  const handleVerify = async (codeStr) => {
    const fullCode = codeStr || code.join('');
    if (fullCode.length !== 6) { toast.error('Enter 6-digit code'); return; }
    setLoading(true);
    try {
      const data = await api.post('/security/2fa/verify', {
        code: fullCode, pending_token: pendingToken, trust_device: trustDevice,
      });
      onVerified(data);
    } catch (e) {
      toast.error(e.message || 'Invalid code');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setResending(true);
    try {
      await api.post('/security/2fa/resend', { pending_token: pendingToken });
      toast.success('New code sent!');
      setCooldown(60);
    } catch (e) { toast.error(e.message || 'Failed to resend'); }
    finally { setResending(false); }
  };

  return (
    <div className="p-6 space-y-5">
      <div className="text-center">
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 ${suspicious ? 'bg-red-500/20' : 'bg-cyan-500/20'}`}>
          {suspicious ? <AlertTriangle className="h-7 w-7 text-red-400" /> : <Shield className="h-7 w-7 text-cyan-400" />}
        </div>
        <h2 className="text-lg font-bold text-white">{suspicious ? 'Suspicious Login Detected' : 'Two-Factor Authentication'}</h2>
        {suspicious && suspiciousReasons?.length > 0 && (
          <div className="mt-2 mb-1 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="suspicious-warning-inline">
            <p className="text-xs text-red-300">This login was flagged. Verify your identity.</p>
            <div className="flex flex-wrap gap-1.5 mt-1.5 justify-center">
              {suspiciousReasons.map((r, i) => (
                <span key={i} className="text-[10px] bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full">
                  {r === 'vpn_proxy' ? 'VPN/Proxy' : r === 'datacenter_ip' ? 'Datacenter IP' : r === 'new_country' ? 'New Country' : r === 'unusual_time' ? 'Unusual Time' : r}
                </span>
              ))}
            </div>
          </div>
        )}
        <p className="text-sm text-slate-400 mt-1">Enter the 6-digit code sent to <span className="text-cyan-400">{emailHint}</span></p>
      </div>

      <div className="flex justify-center gap-2" onPaste={handlePaste} data-testid="2fa-code-inputs-inline">
        {code.map((digit, i) => (
          <input key={i} ref={el => inputRefs.current[i] = el} type="text" inputMode="numeric" maxLength={1} value={digit}
            onChange={e => handleInput(i, e.target.value)} onKeyDown={e => handleKeyDown(i, e)}
            className="w-11 h-13 text-center text-xl font-bold bg-slate-800 border border-slate-600 rounded-lg text-white focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 outline-none transition-colors"
            data-testid={`2fa-inline-digit-${i}`} />
        ))}
      </div>

      <label className="flex items-center gap-3 bg-slate-800/50 rounded-xl p-3 cursor-pointer" data-testid="trust-device-inline-toggle">
        <Smartphone className="h-4 w-4 text-slate-400 flex-shrink-0" />
        <div className="flex-1">
          <span className="text-sm text-slate-200">Trust this device</span>
          <p className="text-[11px] text-slate-500">Skip 2FA for 30 days</p>
        </div>
        <input type="checkbox" checked={trustDevice} onChange={e => setTrustDevice(e.target.checked)} className="w-4 h-4 rounded accent-cyan-500" />
      </label>

      <div className="space-y-2">
        <Button onClick={() => handleVerify()} disabled={loading || code.some(d => d === '')}
          className="w-full bg-cyan-600 hover:bg-cyan-500 h-11 font-medium" data-testid="2fa-inline-verify-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Verify Code
        </Button>
        <div className="flex items-center justify-between">
          <button onClick={onCancel} className="text-sm text-slate-500 hover:text-slate-300 transition-colors" data-testid="2fa-inline-cancel-btn">Cancel</button>
          <button onClick={handleResend} disabled={cooldown > 0 || resending}
            className={`text-sm flex items-center gap-1 transition-colors ${cooldown > 0 ? 'text-slate-600' : 'text-cyan-400 hover:text-cyan-300'}`}
            data-testid="2fa-inline-resend-btn">
            <RefreshCw className={`h-3.5 w-3.5 ${resending ? 'animate-spin' : ''}`} />
            {cooldown > 0 ? `Resend (${cooldown}s)` : 'Resend Code'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TwoFactorModal({ isOpen, pendingToken, emailHint, onVerified, onCancel, suspicious, suspiciousReasons }) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (isOpen && inputRefs.current[0]) {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
    if (isOpen) {
      setCode(['', '', '', '', '', '']);
      setCooldown(60);
    }
  }, [isOpen]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleInput = (idx, value) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...code];
    next[idx] = value.slice(-1);
    setCode(next);
    if (value && idx < 5) inputRefs.current[idx + 1]?.focus();
    // Auto-submit when all 6 digits entered
    if (value && idx === 5 && next.every(d => d !== '')) {
      handleVerify(next.join(''));
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const digits = pasted.split('');
      setCode(digits);
      inputRefs.current[5]?.focus();
      handleVerify(pasted);
      e.preventDefault();
    }
  };

  const handleVerify = async (codeStr) => {
    const fullCode = codeStr || code.join('');
    if (fullCode.length !== 6) { toast.error('Enter 6-digit code'); return; }
    setLoading(true);
    try {
      const data = await api.post('/security/2fa/verify', {
        code: fullCode, pending_token: pendingToken, trust_device: trustDevice,
      });
      onVerified(data);
    } catch (e) {
      toast.error(e.message || 'Invalid code');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setResending(true);
    try {
      await api.post('/security/2fa/resend', { pending_token: pendingToken });
      toast.success('New code sent!');
      setCooldown(60);
    } catch (e) {
      toast.error(e.message || 'Failed to resend');
    } finally {
      setResending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => onCancel()}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm p-0 overflow-hidden">
        <div className="p-6 space-y-5">
          {/* Header */}
          <div className="text-center">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 ${suspicious ? 'bg-red-500/20' : 'bg-cyan-500/20'}`}>
              {suspicious ? <AlertTriangle className="h-7 w-7 text-red-400" /> : <Shield className="h-7 w-7 text-cyan-400" />}
            </div>
            <h2 className="text-lg font-bold text-white">
              {suspicious ? 'Suspicious Login Detected' : 'Two-Factor Authentication'}
            </h2>
            {suspicious && (
              <div className="mt-2 mb-1 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="suspicious-warning">
                <p className="text-xs text-red-300">
                  This login was flagged as suspicious. Verify your identity to continue.
                </p>
                {suspiciousReasons && suspiciousReasons.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1.5 justify-center">
                    {suspiciousReasons.map((r, i) => (
                      <span key={i} className="text-[10px] bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full">
                        {r === 'vpn_proxy' ? 'VPN/Proxy' : r === 'datacenter_ip' ? 'Datacenter IP' : r === 'new_country' ? 'New Country' : r === 'unusual_time' ? 'Unusual Time' : r}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            <p className="text-sm text-slate-400 mt-1">
              Enter the 6-digit code sent to <span className="text-cyan-400">{emailHint}</span>
            </p>
          </div>

          {/* Code Input */}
          <div className="flex justify-center gap-2" onPaste={handlePaste} data-testid="2fa-code-inputs">
            {code.map((digit, i) => (
              <input
                key={i}
                ref={el => inputRefs.current[i] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleInput(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                className="w-11 h-13 text-center text-xl font-bold bg-slate-800 border border-slate-600 rounded-lg text-white focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 outline-none transition-colors"
                data-testid={`2fa-digit-${i}`}
              />
            ))}
          </div>

          {/* Trust device */}
          <label className="flex items-center gap-3 bg-slate-800/50 rounded-xl p-3 cursor-pointer" data-testid="trust-device-toggle">
            <Smartphone className="h-4 w-4 text-slate-400 flex-shrink-0" />
            <div className="flex-1">
              <span className="text-sm text-slate-200">Trust this device</span>
              <p className="text-[11px] text-slate-500">Skip 2FA for 30 days</p>
            </div>
            <input
              type="checkbox"
              checked={trustDevice}
              onChange={e => setTrustDevice(e.target.checked)}
              className="w-4 h-4 rounded accent-cyan-500"
            />
          </label>

          {/* Actions */}
          <div className="space-y-2">
            <Button
              onClick={() => handleVerify()}
              disabled={loading || code.some(d => d === '')}
              className="w-full bg-cyan-600 hover:bg-cyan-500 h-11 font-medium"
              data-testid="2fa-verify-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Verify Code
            </Button>
            <div className="flex items-center justify-between">
              <button onClick={onCancel} className="text-sm text-slate-500 hover:text-slate-300 transition-colors" data-testid="2fa-cancel-btn">
                Cancel
              </button>
              <button
                onClick={handleResend}
                disabled={cooldown > 0 || resending}
                className={`text-sm flex items-center gap-1 transition-colors ${cooldown > 0 ? 'text-slate-600' : 'text-cyan-400 hover:text-cyan-300'}`}
                data-testid="2fa-resend-btn"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${resending ? 'animate-spin' : ''}`} />
                {cooldown > 0 ? `Resend (${cooldown}s)` : 'Resend Code'}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
